import { ROOM_RULES } from "@meleeroyale/shared";
import * as v from "valibot";

/** JSON control messages travel as WebSocket text frames. */
const nickname = v.pipe(v.string(), v.trim(), v.minLength(1), v.maxLength(16));
const pin = v.pipe(v.string(), v.regex(/^\d{4}$/));
const mode = v.picklist(["practice", "custom"]);
const count = (min: number, max: number) =>
  v.pipe(v.number(), v.integer(), v.minValue(min), v.maxValue(max));

export const JoinMessage = v.object({
  t: v.literal("join"),
  protocol: v.number(),
  simHash: v.string(),
  /** Hash of the binary layout the client was built with (protocol/wire.ts). */
  wire: v.string(),
  nickname,
  token: v.string(),
});
export const PingMessage = v.object({ t: v.literal("ping"), c: v.number() });
/** The room's host asks to start the fight (custom sessions). */
export const StartMessage = v.object({ t: v.literal("start") });
export const ClientControl = v.variant("t", [JoinMessage, PingMessage, StartMessage]);
export type ClientControl = v.InferOutput<typeof ClientControl>;

export const RosterEntry = v.object({ id: v.number(), nickname: v.string(), isBot: v.boolean() });
export type RosterEntry = v.InferOutput<typeof RosterEntry>;

export const WaitingPlayer = v.object({
  nickname: v.string(),
  host: v.boolean(),
  you: v.boolean(),
});
export type WaitingPlayer = v.InferOutput<typeof WaitingPlayer>;

/** The waiting room, sent to each player whenever it changes and until the match starts. */
export const WaitingMessage = v.object({
  t: v.literal("waiting"),
  mode,
  minHumans: v.number(),
  maxHumans: v.number(),
  bots: v.number(),
  locked: v.boolean(),
  players: v.array(WaitingPlayer),
  /** Enough humans are in for the host to start. */
  canStart: v.boolean(),
  youAreHost: v.boolean(),
  /** Seconds until an unstarted room closes. */
  closesIn: v.number(),
});
export type WaitingMessage = v.InferOutput<typeof WaitingMessage>;

/** Sent to each player when the match starts (and on a reconnect into a running match). */
export const JoinedMessage = v.object({
  t: v.literal("joined"),
  id: v.number(),
  protocol: v.number(),
  sessionSize: v.number(),
  seed: v.number(),
  tick: v.number(),
  fightStartTick: v.number(),
  fighters: v.array(RosterEntry),
});
export const RosterMessage = v.object({ t: v.literal("roster"), fighters: v.array(RosterEntry) });
export const EventsMessage = v.object({
  t: v.literal("events"),
  tick: v.number(),
  events: v.array(v.record(v.string(), v.unknown())),
});
export const PongMessage = v.object({
  t: v.literal("pong"),
  c: v.number(),
  s: v.number(),
  tick: v.number(),
});
export const ErrorMessage = v.object({
  t: v.literal("error"),
  code: v.string(),
  message: v.string(),
});
export const ServerControl = v.variant("t", [
  WaitingMessage,
  JoinedMessage,
  RosterMessage,
  EventsMessage,
  PongMessage,
  ErrorMessage,
]);
export type ServerControl = v.InferOutput<typeof ServerControl>;

// ---- HTTP ------------------------------------------------------------------

/** POST /api/practice: a solo room against bots that starts as soon as its player connects. */
export const PracticeRequest = v.object({
  nickname,
  bots: count(ROOM_RULES.practice.minBots, ROOM_RULES.practice.maxBots),
});
export type PracticeRequest = v.InferOutput<typeof PracticeRequest>;

/** POST /api/sessions: a custom room; its creator is the host. */
export const CreateSessionRequest = v.object({
  nickname,
  minHumans: count(ROOM_RULES.custom.minHumans, ROOM_RULES.custom.maxHumans),
  bots: count(0, ROOM_RULES.custom.maxBots),
  pin: v.optional(pin),
});
export type CreateSessionRequest = v.InferOutput<typeof CreateSessionRequest>;

/** POST /api/sessions/:roomId/join */
export const JoinSessionRequest = v.object({ nickname, pin: v.optional(pin) });
export type JoinSessionRequest = v.InferOutput<typeof JoinSessionRequest>;

/** Answer to practice, create and join: a seat token for the room's WebSocket. */
export const SessionResponse = v.object({
  roomId: v.string(),
  token: v.string(),
  wsPath: v.string(),
  mode,
});
export type SessionResponse = v.InferOutput<typeof SessionResponse>;

/** One open custom session in GET /api/sessions. The PIN itself never leaves the room. */
export const SessionListing = v.object({
  roomId: v.string(),
  host: v.string(),
  humans: v.number(),
  minHumans: v.number(),
  maxHumans: v.number(),
  bots: v.number(),
  locked: v.boolean(),
});
export type SessionListing = v.InferOutput<typeof SessionListing>;
export const SessionList = v.object({ sessions: v.array(SessionListing) });
export type SessionList = v.InferOutput<typeof SessionList>;

export function parseClientControl(text: string): ClientControl | null {
  return parseJson(ClientControl, text);
}
export function parseServerControl(text: string): ServerControl | null {
  return parseJson(ServerControl, text);
}

function parseJson<S extends v.GenericSchema>(schema: S, text: string): v.InferOutput<S> | null {
  let json: unknown;
  try {
    json = JSON.parse(text);
  } catch {
    return null;
  }
  const result = v.safeParse(schema, json);
  return result.success ? result.output : null;
}
